import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Plus } from "lucide-react";
import { PageHeader } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, formatTime12 } from "../lib/api";

const STATUSES = ["AVAILABLE", "OCCUPIED", "RESERVED", "MAINTENANCE", "BLOCKED"];

export default function Seats() {
  const [seats, setSeats] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [shiftFilter, setShiftFilter] = useState("ALL");
  const [show, setShow] = useState(false);
  const [bulkShow, setBulkShow] = useState(false);
  const [number, setNumber] = useState("");
  const [bulk, setBulk] = useState({ prefix: "A", start: 1, count: 10 });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editStatus, setEditStatus] = useState("AVAILABLE");

  const load = async () => {
    try {
      const [seatsRes, shiftsRes] = await Promise.all([api.get("/seats"), api.get("/shifts")]);
      setSeats(seatsRes.data.data);
      setShifts(shiftsRes.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(
    () => seats.reduce((acc, seat) => ({ ...acc, [seat.status]: (acc[seat.status] || 0) + 1 }), {}),
    [seats]
  );
  const visible = filter === "ALL" ? seats : seats.filter((seat) => seat.status === filter);

  const selectedShift = shiftFilter === "ALL" ? null : shifts.find((shift) => shift.id === shiftFilter);
  const occupantFor = (seat) => {
    if (!selectedShift) return null;
    return (seat.assignments || []).find((assignment) => (assignment.shift_ids || []).includes(selectedShift.id)) || null;
  };
  const freeForShift = selectedShift ? seats.filter((seat) => !occupantFor(seat) && seat.status !== "MAINTENANCE" && seat.status !== "BLOCKED").length : null;

  const addSeat = async (event) => {
    event.preventDefault();
    try {
      await api.post("/seats", { number });
      setNumber("");
      setShow(false);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const addBulk = async (event) => {
    event.preventDefault();
    try {
      await api.post("/seats/bulk", { ...bulk, count: Number(bulk.count), start: Number(bulk.start) });
      setBulkShow(false);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const changeSeat = async () => {
    try {
      await api.patch(`/seats/${editing.id}`, { status: editStatus });
      setEditing(null);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const deleteSeat = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete seat ${editing.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/seats/${editing.id}`);
      setEditing(null);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="SPACE MANAGEMENT"
        title="Seat map"
        description="Live floor plan with every seat's status and student assignment."
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <button className="button button-outline" onClick={() => setBulkShow(true)} data-testid="bulk-seat-button">
              Bulk add
            </button>
            <button className="button button-primary" onClick={() => setShow(true)} data-testid="add-seat-button">
              <Plus size={16} /> Add seat
            </button>
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}

      <div className="seat-summary">
        <div><span>Total</span><strong>{seats.length}</strong></div>
        <div><span>Available</span><strong className="green-text">{counts.AVAILABLE || 0}</strong></div>
        <div><span>Occupied</span><strong className="red-text">{counts.OCCUPIED || 0}</strong></div>
        <div><span>Maintenance</span><strong>{counts.MAINTENANCE || 0}</strong></div>
      </div>

      <section className="content-card seat-card">
        <div className="card-heading">
          <div>
            <span className="card-kicker">LIVE FLOOR PLAN</span>
            <h2>{visible.length} seats</h2>
          </div>
          <div className="toolbar-actions">
            <select value={shiftFilter} onChange={(event) => setShiftFilter(event.target.value)} data-testid="seat-shift-filter">
              <option value="ALL">All shifts</option>
              {shifts.map((shift) => (
                <option key={shift.id} value={shift.id}>{shift.name} ({formatTime12(shift.start_time)} – {formatTime12(shift.end_time)})</option>
              ))}
            </select>
            <select value={filter} onChange={(event) => setFilter(event.target.value)} data-testid="seat-filter">
              <option value="ALL">All seats</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="seat-legend">
          <span><i className="seat-available" />Available</span>
          <span><i className="seat-occupied" />Occupied</span>
          <span><i className="seat-reserved" />Reserved</span>
          <span><i className="seat-maintenance" />Maintenance</span>
        </div>
        <div className="seat-grid" data-testid="seat-grid">
          {selectedShift ? (
            <p style={{ margin: "0 0 8px", color: "#16a34a", fontWeight: 600, fontSize: 13 }} data-testid="seat-shift-summary">
              {freeForShift} seat{freeForShift === 1 ? "" : "s"} free in {selectedShift.name} ({formatTime12(selectedShift.start_time)} – {formatTime12(selectedShift.end_time)})
            </p>
          ) : null}
          {visible.map((seat) => {
            const occupant = occupantFor(seat);
            const shiftOccupied = !!occupant;
            const tileClass = selectedShift ? (shiftOccupied ? "occupied" : "available") : seat.status.toLowerCase();
            const label = selectedShift
              ? (shiftOccupied ? occupant.student_code || "occupied" : "available")
              : seat.status.toLowerCase();
            return (
              <button
                type="button"
                key={seat.id}
                className={`seat-tile ${tileClass}`}
                title={shiftOccupied ? `${occupant.student_name} (${occupant.student_code})` : ""}
                onClick={() => { setEditing(seat); setEditStatus(seat.status); }}
                data-testid={`seat-${seat.number}`}
              >
                <strong>{seat.number}</strong>
                <small>{label}</small>
              </button>
            );
          })}
        </div>
      </section>

      {show ? (
        <Modal title="Add seat" eyebrow="NEW SEAT" close={() => setShow(false)} testId="add-seat-modal">
          <form className="modal-form" onSubmit={addSeat}>
            <label>Seat number<input type="text" required value={number} onChange={(event) => setNumber(event.target.value)} placeholder="A25" data-testid="seat-number-input" /></label>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-seat-button">
                Add seat <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {bulkShow ? (
        <Modal title="Bulk add seats" eyebrow="QUICK EXPAND" close={() => setBulkShow(false)} testId="bulk-seat-modal">
          <form className="modal-form grid-form" onSubmit={addBulk}>
            <label>Prefix<input type="text" maxLength={4} value={bulk.prefix} onChange={(event) => setBulk({ ...bulk, prefix: event.target.value })} data-testid="bulk-seat-prefix-input" placeholder="max 4 chars" /></label>
            <label>Start<input type="number" min="1" value={bulk.start} onChange={(event) => setBulk({ ...bulk, start: event.target.value })} data-testid="bulk-seat-start-input" /></label>
            <label>Count<input type="number" min="1" max="500" value={bulk.count} onChange={(event) => setBulk({ ...bulk, count: event.target.value })} data-testid="bulk-seat-count-input" /></label>
            <div className="modal-actions grid-span-2">
              <button type="button" className="button button-outline" onClick={() => setBulkShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-bulk-seats-button">
                Create seats <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {editing ? (
        <Modal title={`Seat ${editing.number}`} eyebrow="SEAT CONTROL" close={() => setEditing(null)} testId="seat-detail-modal">
          <div className="modal-form">
            <p style={{ color: "#64748b" }}>Current status: <strong>{editing.status}</strong></p>
            {editing.student_code ? <p>Assigned to <strong>{editing.student_code}</strong></p> : null}
            {(editing.assignments || []).length ? (
              <div data-testid="seat-assignments">
                <p style={{ color: "#64748b", margin: "8px 0 4px" }}><strong>Shift bookings</strong></p>
                {editing.assignments.map((assignment) => (
                  <p key={assignment.student_id} style={{ fontSize: 13, margin: "2px 0" }}>
                    <strong>{assignment.student_name}</strong> ({assignment.student_code}) — {(assignment.shift_names || []).join(", ")}
                  </p>
                ))}
              </div>
            ) : null}
            <label>Change status
              <select value={editStatus} onChange={(event) => setEditStatus(event.target.value)}>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" className="button button-danger" onClick={deleteSeat} disabled={editing.status === "OCCUPIED"} title={editing.status === "OCCUPIED" ? "Release the student first" : "Delete seat"} data-testid="delete-seat-button">
                Delete seat
              </button>
              <button type="button" className="button button-primary" onClick={changeSeat} data-testid="save-seat-status-button">
                Update <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
